/**
 * SentinelPanel — the autonomous-monitor UI (app-specific presentation).
 *
 * The Sentinel engine itself lives in @gridstorm/dataflow-sentinel; this panel
 * is just the demo's UI over it. Pure presentation — the loop lives in the
 * package's useSentinel hook, owned by AnalystPage.
 */

import React from 'react';
import {
  TkxCard, TkxCardHeader, TkxCardBody, TkxBadge, TkxButton, TkxToggle, TkxSelect, TkxInput, TkxAlert,
} from 'tekivex-ui';
import type { Finding, FindingSeverity, SentinelConfig } from '@gridstorm/dataflow-sentinel';

export interface SentinelPanelProps {
  config:         SentinelConfig;
  onConfigChange: (cfg: SentinelConfig) => void;
  webhookUrl:     string;
  onWebhookChange: (url: string) => void;
  findings:       Finding[];
  suppressed:     number;
  onClear:        () => void;
}

const SEVERITY_OPTIONS = [
  { value: 'info',     label: 'Info and above (noisy)' },
  { value: 'warning',  label: 'Warning and above (recommended)' },
  { value: 'critical', label: 'Critical only (quiet)' },
];

function sevBadgeVariant(s: FindingSeverity): 'danger' | 'warning' | 'info' {
  return s === 'critical' ? 'danger' : s === 'warning' ? 'warning' : 'info';
}

export function SentinelPanel({
  config, onConfigChange, webhookUrl, onWebhookChange, findings, suppressed, onClear,
}: SentinelPanelProps) {
  const set = (patch: Partial<SentinelConfig>) => onConfigChange({ ...config, ...patch });

  return (
    <TkxCard variant="elevated" padding="md">
      <TkxCardHeader
        title="🛰 Sentinel — autonomous monitor"
        subtitle={config.enabled
          ? 'Watching the live stream. It investigates anomalies and explains them by itself.'
          : 'Turn on to watch this data for anomalies, investigate the cause, and alert you.'}
        action={
          <TkxToggle
            checked={config.enabled}
            onChange={(c) => set({ enabled: c })}
            label={config.enabled ? 'On' : 'Off'}
            size="md"
          />
        }
      />
      <TkxCardBody>
        {config.enabled && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                <TkxSelect
                  label="Alert on"
                  options={SEVERITY_OPTIONS}
                  value={config.minSeverity}
                  onChange={(v) => set({ minSeverity: v as FindingSeverity })}
                  size="md"
                />
              </div>
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <TkxInput
                  label="Webhook URL (optional)"
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => onWebhookChange(e.target.value)}
                  placeholder="https://your-endpoint.example.com/alerts"
                  hint="POSTs each finding as JSON. From the browser this needs a CORS-enabled endpoint — Slack/Discord webhooks block browser requests (use your own endpoint or a proxy)."
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
            </div>
            <TkxToggle
              checked={config.useAi}
              onChange={(c) => set({ useAi: c })}
              label="Enrich explanations with my configured LLM (the provider set in “Ask the data”)"
              size="md"
            />
          </div>
        )}

        {!config.enabled ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)', fontSize: 13 }}>
            Sentinel is off.
          </div>
        ) : findings.length === 0 ? (
          <TkxAlert variant="info" title="Watching…">
            No findings yet. Sentinel investigates anomalies the moment the engine detects them and
            posts an explained alert here{webhookUrl ? ' and to your webhook' : ''}.
            {suppressed > 0 && <> ({suppressed} low-severity or rate-limited events suppressed.)</>}
          </TkxAlert>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                {findings.length} finding{findings.length === 1 ? '' : 's'}
                {suppressed > 0 && <> · {suppressed} suppressed</>}
              </span>
              <TkxButton variant="ghost" size="sm" onClick={onClear}>Clear</TkxButton>
            </div>
            {findings.map((f) => <FindingCard key={f.id} finding={f} />)}
          </div>
        )}
      </TkxCardBody>
    </TkxCard>
  );
}

function FindingCard({ finding }: { finding: Finding }) {
  const f = finding;
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${f.severity === 'critical' ? 'var(--red)' : f.severity === 'warning' ? 'var(--yellow)' : 'var(--accent)'}`,
        borderRadius: 8,
        background: 'var(--bg)',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <TkxBadge variant={sevBadgeVariant(f.severity)} size="sm">{f.severity}</TkxBadge>
        <strong style={{ fontSize: 13.5 }}>{f.title}</strong>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
          {Math.round(f.confidence * 100)}% conf{f.enriched && <> · ✨ AI</>}
        </span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{f.cause}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {f.evidence.map((e, i) => (
          <span key={i} style={{
            fontFamily: 'var(--mono)', fontSize: 10.5, padding: '2px 7px',
            background: 'var(--bg-3)', color: 'var(--text-2)', borderRadius: 4,
          }}>
            {e.label}: {e.value}
          </span>
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text)', display: 'flex', gap: 6 }}>
        <span aria-hidden>→</span>
        <span><strong>Action:</strong> {f.suggestedAction}</span>
      </div>
    </div>
  );
}
